import {useCallback, useEffect, useRef, useState} from 'react';
import {Slide, SlidingWizard, SlidingWizardHandle} from '../../components/sliding_form_wizard/sliding_wizard.tsx';
import styles from './appointment.module.scss';
import {Form, useSearchParams} from 'react-router-dom';
import {VehicleSelectContent, VehicleSelectFooter, VehicleSelectTitle} from './components/vehicle_select.tsx';
import {useAppSelector} from '../../store/store.ts';
import {LocationSelectContent, LocationSelectFooter, LocationSelectTitle} from './components/location_select.tsx';
import {
    AppointmentDetailsContent,
    AppointmentDetailsFooter,
    AppointmentDetailsTitle,
} from './components/appointment_details.tsx';
import {DatePickerContent, DatePickerFooter, DatePickerTitle} from './components/date_picker.tsx';
import {Main} from '../../components/main/main.tsx';
import {addMonths, endOfMonth, format, startOfMonth} from 'date-fns';
import {Planner} from '../../network/planner.ts';
import {AvailabilityType, CustomerType} from '../../gql/pwa/graphql.ts';
import {VehicleType} from '../../gql/customer/graphql.ts';
import {MileageContent, MileageFooter, MileageTitle} from './components/mileage.tsx';
import {SummaryContent, SummaryFooter, SummaryTitle} from './components/summary.tsx';
import {TimePickerContent, TimePickerFooter, TimePickerTitle} from './components/time_picker.tsx';
import {SelectVehicleModal} from './components/modals/select_vehicle_modal.tsx';
import {SelectLocationModal} from './components/modals/select_location_modal.tsx';

function initialVehicle(vehicles: (VehicleType | null)[], defaultVehicleId?: number): VehicleType | null {
    return vehicles?.find((v) => v?.id === defaultVehicleId) || vehicles[0] || null;
}

export function Appointment() {
    const sliderRef = useRef<SlidingWizardHandle>(null);

    const [currentSlide, setCurrentSlide] = useState(0);
    const [editingStates, setEditingStates] = useState<Record<number, boolean>>({});

    const isEditing = editingStates[currentSlide] ?? false;

    const setSlideEditing = (value: boolean) => {
        setEditingStates((prev) => ({...prev, [currentSlide]: value}));
    };

    const next = () => {
        setCurrentSlide((p) => p + 1);
        sliderRef.current?.slideNext();
    };

    const prev = () => {
        setCurrentSlide((p) => Math.max(p - 1, 0));
        sliderRef.current?.slidePrev();
    };

    const [params] = useSearchParams();
    const urlVehicleId = params.get('vehicle_id');
    const urlLocationId = params.get('location_id');

    const {customerInfo, appConfig} = useAppSelector((s) => s);
    const customer = customerInfo.me?.customer as CustomerType;

    const vehicles = (customer?.vehicles as VehicleType[]) || [];
    const locations = appConfig.locations;

    const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
        initialVehicle(
            vehicles,
            (urlVehicleId && Number.parseInt(urlVehicleId, 10)) || (customer?.default_vehicle_id ?? undefined),
        )?.id || null,
    );

    const [locationId, setLocationId] = useState<number | null>(
        locations?.find(
            (location) =>
                location?.id ===
                ((urlLocationId && Number.parseInt(urlLocationId, 10)) || customer?.default_location_id),
        )?.id ||
            locations?.[0]?.id ||
            null,
    );

    const selectedVehicle = vehicles.find((vehicle) => vehicle?.id === selectedVehicleId) || null;
    const selectedVehicleLicensePlate = selectedVehicle?.identifiers?.find(
        (vehicle) => vehicle?.type === 'license_plate',
    )?.value;
    const selectedLocation = locations?.find((location) => location?.id === locationId) || null;
    const specialty = selectedLocation?.specialties?.[0];
    const hasMainActivities = (specialty?.activities || []).some((activity) => activity?.type === 'main');

    const [remarks, setRemarks] = useState('');
    const [mainActivityId, setMainActivityId] = useState<string | null>(null);
    const [subActivityIds, setSubActivityIds] = useState<string[]>([]);
    const [replacementVehicleId, setReplacementVehicleId] = useState<string | null>(null);
    const [availabilities, setAvailabilities] = useState<AvailabilityType[]>([]);
    const [availableTimes, setAvailableTimes] = useState<string[]>([]);
    const [appointmentDate, setAppointmentDate] = useState<Date | null>(null);
    const [time, setTime] = useState<string | null>(null);
    const hasSelectedActivities = hasMainActivities ? !!mainActivityId : subActivityIds.length > 0;

    const [mileage, setMileage] = useState<number | null>(selectedVehicle?.mileage?.valueOf() || null);

    useEffect(() => {
        setMileage(selectedVehicle?.mileage?.valueOf() || null);
    }, [selectedVehicleId]);

    const fetchAvailabilities = useCallback(async () => {
        if (!locationId || !hasSelectedActivities || !replacementVehicleId) return;

        const start = startOfMonth(new Date());
        const end = endOfMonth(addMonths(start, 6));

        const res = await Planner.availabilities(
            locationId,
            start,
            end,
            mainActivityId,
            replacementVehicleId,
            subActivityIds,
        );

        const data = res.data.locations?.[0]?.specialties?.[0]?.availabilities;
        if (data) setAvailabilities(data as AvailabilityType[]);
    }, [locationId, hasSelectedActivities, mainActivityId, replacementVehicleId, subActivityIds]);

    const fetchTimes = useCallback(
        async (date?: Date) => {
            const d = date || appointmentDate;
            if (!locationId || !hasSelectedActivities || !replacementVehicleId || !d) return;

            const res = await Planner.availableTimes(locationId, d, mainActivityId, subActivityIds);
            const data = res.data.locations?.[0]?.specialties?.[0]?.available_times;

            if (data) setAvailableTimes(data as string[]);
        },
        [appointmentDate, locationId, hasSelectedActivities, mainActivityId, replacementVehicleId, subActivityIds],
    );

    useEffect(() => {
        void fetchAvailabilities();
    }, [fetchAvailabilities]);

    const slides: Slide[] = [
        {
            title: <VehicleSelectTitle brand={selectedVehicle?.brand} />,
            onPreviousClick: null,
            content: <VehicleSelectContent isEditing={isEditing} vehicles={vehicles} vehicleId={selectedVehicleId} />,
            footer: (
                <VehicleSelectFooter
                    isEditing={isEditing}
                    isDisabled={!selectedVehicleId}
                    setIsEditing={() => setSlideEditing(true)}
                    onNextSlide={next}
                    vehicles={vehicles}
                />
            ),
        },
        {
            title: <LocationSelectTitle locationId={locationId} />,
            onPreviousClick: prev,
            content: <LocationSelectContent isEditing={isEditing} locationId={locationId} />,
            footer: (
                <LocationSelectFooter
                    isEditing={isEditing}
                    setIsEditing={() => setSlideEditing(true)}
                    onPrevSlide={prev}
                    onNextSlide={next}
                />
            ),
        },
        {
            title: <AppointmentDetailsTitle />,
            onPreviousClick: prev,
            disableNextSlide: !hasSelectedActivities || !replacementVehicleId,
            content: (
                <AppointmentDetailsContent
                    isEditing={isEditing}
                    onEditClick={() => setSlideEditing(true)}
                    onMainActivityIdChange={setMainActivityId}
                    onyReplacementVehicleIdChange={setReplacementVehicleId}
                    onSubActivityIdsChange={setSubActivityIds}
                    mainActivityId={mainActivityId}
                    subActivityIds={subActivityIds}
                    replacementVehicleId={replacementVehicleId}
                    locationId={locationId}
                    remarks={remarks}
                    onRemarksChange={setRemarks}
                />
            ),
            footer: (
                <AppointmentDetailsFooter
                    onPreviousSlide={prev}
                    onNextSlide={() => {
                        setSlideEditing(false);
                        next();
                    }}
                    nextIsDisabled={!hasSelectedActivities || !replacementVehicleId}
                />
            ),
        },
        {
            title: <DatePickerTitle />,
            onPreviousClick: prev,
            disableNextSlide: !appointmentDate,
            content: (
                <DatePickerContent
                    availabilities={availabilities}
                    date={appointmentDate}
                    onDateChange={(d) => {
                        setAppointmentDate(d);
                        setTime(null);
                        void fetchTimes(d);
                    }}
                />
            ),
            footer: <DatePickerFooter onPreviousSlide={prev} onNextSlide={next} nextIsDisabled={!appointmentDate} />,
        },
        {
            title: <TimePickerTitle />,
            onPreviousClick: prev,
            disableNextSlide: !time,
            content: (
                <TimePickerContent
                    availableTimes={availableTimes}
                    time={time}
                    onTimeChange={setTime}
                    isEditing={isEditing}
                    onEditClick={() => setSlideEditing(true)}
                />
            ),
            footer: <TimePickerFooter onPreviousSlide={prev} onNextSlide={next} nextIsDisabled={!time} />,
        },
        {
            title: <MileageTitle />,
            onPreviousClick: prev,
            disableNextSlide: !mileage,
            content: <MileageContent mileage={mileage} brand={selectedVehicle?.brand} onMileageChange={setMileage} />,
            footer: <MileageFooter onPreviousSlide={prev} onNextSlide={next} nextIsDisabled={!mileage} />,
        },
        {
            title: <SummaryTitle />,
            onPreviousClick: prev,
            content: (
                <SummaryContent
                    locationId={locationId}
                    vehicle={selectedVehicle}
                    mileage={mileage}
                    mainActivityId={mainActivityId}
                    subActivityIds={subActivityIds}
                    replacementVehicleId={replacementVehicleId}
                    date={appointmentDate}
                    time={time}
                    remarks={remarks}
                />
            ),
            footer: <SummaryFooter onPreviousSlide={prev} />,
        },
    ];

    return (
        <Main>
            <div className={styles.appointment}>
                <Form method="post" className={styles.appointment_content}>
                    <SlidingWizard ref={sliderRef} slides={slides} isEditing={isEditing} />

                    {isEditing && currentSlide === 0 && (
                        <SelectVehicleModal
                            onCloseModal={() => setSlideEditing(false)}
                            vehicles={vehicles}
                            vehicleId={selectedVehicleId}
                            onVehicleIdChange={(id) => {
                                setSelectedVehicleId(id);
                                setSlideEditing(false);
                            }}
                        />
                    )}
                    {isEditing && currentSlide === 1 && (
                        <SelectLocationModal
                            onCloseModal={() => setSlideEditing(false)}
                            locations={locations}
                            locationId={locationId}
                            onLocationIdChange={(id) => {
                                setLocationId(id);
                                setSlideEditing(false);
                            }}
                        />
                    )}

                    <input name="location_id" type="hidden" value={locationId || ''} />
                    <input name="specialty_id" type="hidden" value={specialty?.id || ''} />
                    <input name="vehicle_id" type="hidden" value={selectedVehicleId || ''} />
                    <input name="license_plate" type="hidden" value={selectedVehicleLicensePlate || ''} />
                    <input name="mileage" type="hidden" value={mileage || ''} />
                    <input name="main_activity_id" type="hidden" value={mainActivityId || ''} />
                    <input name="sub_activity_ids" type="hidden" value={subActivityIds.join(',')} />
                    <input name="replacement_vehicle_id" type="hidden" value={replacementVehicleId || ''} />
                    <input name="remarks" type="hidden" value={remarks} />
                    <input
                        name="date"
                        type="hidden"
                        value={appointmentDate ? format(appointmentDate, 'yyyy-MM-dd') : ''}
                    />
                    <input name="time" type="hidden" value={time || ''} />
                </Form>
            </div>
        </Main>
    );
}
